import type { ReactNode } from "react";
import {
  resolveTeacherExpectedStudents,
  resolveTeacherExpectedParticipantCount,
  type TeacherExpectedStudentDto,
  type TeacherScheduleItemDto
} from "@madamuh/domain";
import { Link } from "react-router-dom";
import {
  formatTenantDateTimeRange,
  formatTenantDateWithWeekday,
  formatTenantTime,
} from "../../shared/lib/tenantDateTime";
import { GENERIC_TENANT_LABELS, type TenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";

export function buildTeacherPageLinks(hasAdminRole: boolean, labels: TenantLabels = GENERIC_TENANT_LABELS) {
  return [
    { to: "/t/:tenantSlug/teacher", label: `${labels.teacher}ホーム` },
    { to: "/t/:tenantSlug/teacher/absence-request/new", label: "欠勤申請" },
    ...(hasAdminRole ? [{ to: "/t/:tenantSlug/teacher/admin", label: "管理者" }] : [])
  ];
}

export function TeacherAccessState({
  routeStatus,
  sessionStatus,
  errorMessage,
  hasTeacherRole
}: {
  routeStatus: "checking" | "authenticated" | "redirecting" | "error";
  sessionStatus: "idle" | "loading" | "authenticated" | "unauthenticated" | "error";
  errorMessage: string | null;
  hasTeacherRole: boolean;
}) {
  if (routeStatus === "checking" || sessionStatus === "loading") {
    return <p>セッションを確認しています。</p>;
  }

  if (routeStatus === "redirecting") {
    return <p>利用開始へ移動しています。</p>;
  }

  if (sessionStatus === "error" || routeStatus === "error") {
    return <p>セッション確認に失敗しました。{errorMessage}</p>;
  }

  if (routeStatus === "authenticated" && !hasTeacherRole) {
    return <p>この画面は講師のみ利用できます。</p>;
  }

  return null;
}

export function formatTeacherDate(value: string, timeZone: string): string {
  return formatTenantDateWithWeekday(value, timeZone);
}

export function formatTeacherTimeRange(startAt: string, endAt: string, timeZone: string): string {
  return `${formatTenantTime(startAt, timeZone)}〜${formatTenantTime(endAt, timeZone)}`;
}

export function formatTeacherDateTimeRange(startAt: string, endAt: string, timeZone: string): string {
  return formatTenantDateTimeRange(startAt, endAt, timeZone);
}

function formatTeacherExpectedStudentName(student: TeacherExpectedStudentDto): string {
  return student.isAbsent ? `${student.displayName}（休み）` : student.displayName;
}

function buildTeacherFlagBadges(item: TeacherScheduleItemDto): Array<{ label: string; background: string }> {
  const badges: Array<{ label: string; background: string }> = [];

  if (item.displayFlags.isCancelled) {
    badges.push({ label: "休講", background: "#fff1f1" });
  }

  if (item.displayFlags.isRescheduled) {
    badges.push({ label: "振替", background: "#fff6e6" });
  }

  if (item.displayFlags.isStudioTbd) {
    badges.push({ label: "会場未定", background: "#f6f6f6" });
  }

  if (item.displayFlags.isTeacherTbd) {
    badges.push({ label: "講師未定", background: "#f6f6f6" });
  }

  if (item.displayFlags.isSubstitute) {
    badges.push({ label: "代講", background: "#eef6ff" });
  }

  return badges;
}

export function TeacherScheduleItemCard({
  item,
  tenantSlug,
  timeZone,
  showOccurrenceDate = false,
  showExpectedStudents = false,
  expectedStudentsTitle = "参加予定生徒",
  showTeacherSummary = false,
  showOperatorNote = false,
  showDetailLink = true,
  actions = null,
  labels = GENERIC_TENANT_LABELS
}: {
  item: TeacherScheduleItemDto;
  tenantSlug: string;
  timeZone: string;
  showOccurrenceDate?: boolean;
  showExpectedStudents?: boolean;
  expectedStudentsTitle?: string;
  showTeacherSummary?: boolean;
  showOperatorNote?: boolean;
  showDetailLink?: boolean;
  actions?: ReactNode;
  labels?: TenantLabels;
}) {
  const badges = buildTeacherFlagBadges(item);
  const expectedParticipantCount = resolveTeacherExpectedParticipantCount(item);
  const expectedStudents = resolveTeacherExpectedStudents(item);

  return (
    <article
      style={{
        border: "1px solid #e5e5e5",
        borderRadius: 12,
        padding: 12,
        display: "grid",
        gap: 8
      }}
    >
      <div style={{ display: "grid", gap: 4 }}>
        <strong>{item.lessonTitle}</strong>
        {showOccurrenceDate ? (
          <div style={{ fontSize: 14, opacity: 0.8 }}>
            {formatTeacherDateTimeRange(item.startAt, item.endAt, timeZone)}
          </div>
        ) : (
          <div style={{ fontSize: 14, opacity: 0.8 }}>
            {formatTeacherTimeRange(item.startAt, item.endAt, timeZone)}
          </div>
        )}
      </div>

      {item.studioNameSnapshot ? <div style={{ fontSize: 14 }}>{labels.studio}: {item.studioNameSnapshot}</div> : null}
      {item.displayFlags.isStudioTbd && !item.studioNameSnapshot ? <div style={{ fontSize: 14 }}>{labels.studio}: 未定</div> : null}
      <div style={{ fontSize: 14 }}>受講予定: {expectedParticipantCount ?? "-"}名</div>

      {showExpectedStudents ? (
        <div style={{ display: "grid", gap: 4, fontSize: 14 }}>
          <strong style={{ fontSize: 14 }}>{expectedStudentsTitle}</strong>
          {expectedStudents.length === 0 ? (
            <div>受講予定の{labels.student}はいません。</div>
          ) : (
            expectedStudents.map((student) => (
              <div key={`${item.occurrenceId}:${student.studentId}`}>
                {formatTeacherExpectedStudentName(student)}
              </div>
            ))
          )}
        </div>
      ) : null}

      {showTeacherSummary ? (
        <div style={{ display: "grid", gap: 4, fontSize: 14 }}>
          <div>
            担当{labels.teacher}: {item.displayFlags.showTeacherNames ? item.teacherNameSnapshots.join(", ") : "未定"}
          </div>
          <div>担当状態: {describeStatus(item.teacherAssignmentStatus)}</div>
        </div>
      ) : null}

      {item.publicNote ? <div style={{ fontSize: 14 }}>共有メモ: {item.publicNote}</div> : null}
      {showOperatorNote && item.operatorNote ? <div style={{ fontSize: 14 }}>運営メモ: {item.operatorNote}</div> : null}

      {badges.length > 0 ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
          {badges.map((badge) => (
            <span
              key={`${item.occurrenceId}:${badge.label}`}
              style={{ padding: "4px 8px", borderRadius: 999, background: badge.background }}
            >
              {badge.label}
            </span>
          ))}
        </div>
      ) : null}

      {showDetailLink || actions ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {showDetailLink ? (
            <Link to={`/t/${tenantSlug}/teacher/occurrences/${item.occurrenceId}`}>詳細</Link>
          ) : null}
          {actions}
        </div>
      ) : null}
    </article>
  );
}
